import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

import { axiosConfig } from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";
import usePartnersWS from "../../hooks/usePartnersWS";
import { partnersWSManager } from "../../services/partnersWSManager";

import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// ── palette (brand) ──────────────────────────────────────────────────────
const pageBg = "#eef2f7";
const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const navy = "#04343a";
const brandGreen = "#016551";
const negRed = "#dc2626";
const textMain = "#1e293b";
const textMuted = "#64748b";

const SIGNAL_COLORS = { long: brandGreen, short: negRed };

const num = (n) => (typeof n === "number" && !isNaN(n) ? n : Number(n) || 0);
const fmtRs = (n) => (n === null || n === undefined || n === "" ? "—" : `₹${num(n).toLocaleString("en-IN")}`);

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} || ${hh}:${min}`;
};

const fmtExpiry = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const Chip = ({ label, color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 9.5,
      fontWeight: 700,
      color: "#fff",
      background: color,
      borderRadius: "999px",
      padding: "2px 7px",
      whiteSpace: "nowrap",
      textTransform: "uppercase",
    }}
  >
    {label}
  </Box>
);

const STATUS_COLORS = { Open: brandGreen, Exited: textMuted };

const exitReasonLabel = (reason) =>
  reason ? reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const COLUMNS = [
  { label: "S.No", width: "32px" },
  { label: "", width: "20px" },
  { label: "Hold By", width: "80px" },
  { label: "Exit Reason", width: "95px" },
  { label: "Signal", width: "75px" },
  { label: "Symbol", width: "150px" },
  { label: "Lot Size", width: "75px" },
  { label: "Entry Price", width: "90px" },
  { label: "LTP", width: "90px" },
  { label: "P&L", width: "90px" },
  { label: "Target", width: "80px" },
  { label: "SL", width: "80px" },
  { label: "Entry Time LTP", width: "90px" },
  { label: "Entry Date & Time", width: "150px" },
  { label: "Profit as per High/Low", width: "120px" },
  { label: "Profit as per Low", width: "100px" },
  { label: "Loss as per Low", width: "100px" },
  { label: "Exit Date & Time", width: "150px" },
  { label: "Final P&L", width: "90px" },
  { label: "Exit Price", width: "90px" },
  { label: "Action", width: "75px" },
];

const OPEN_ONLY_HIDE = ["Exit Reason", "Exit Date & Time", "Final P&L", "Exit Price"];
const EXITED_ONLY_HIDE = ["Action"];

// previous_data rows don't carry ltp/pl, and exited trades don't track live — hide those
// columns entirely unless we're on Today + Open. Exit-related columns only apply once a
// trade has exited; Action (Take) only applies while it's still open.
const getVisibleColumns = (showLiveColumns, isExitedTab) =>
  COLUMNS.filter((c) => {
    if (!showLiveColumns && (c.label === "LTP" || c.label === "P&L")) return false;
    if (isExitedTab && EXITED_ONLY_HIDE.includes(c.label)) return false;
    if (!isExitedTab && OPEN_ONLY_HIDE.includes(c.label)) return false;
    return true;
  });

const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

const POST_DASHBOARD_URL = "/posting-future-recommendation-to-future-dashboard/";

// Shows a field at full opacity even though it's read-only — `disabled` dims
// MUI inputs, `InputProps.readOnly` on a plain TextField doesn't.
const ReadOnlyField = ({ label, value }) => (
  <TextField
    label={label}
    value={value ?? "—"}
    size="small"
    fullWidth
    InputProps={{ readOnly: true }}
    sx={{
      "& .MuiInputBase-input": { color: textMain, WebkitTextFillColor: textMain },
      "& .MuiOutlinedInput-root": { background: "#f8fafc" },
    }}
  />
);

const FutureRow = ({ t, i, isExpanded, toggleRow, takingId, handleTake, onPost, showLiveColumns, isExitedTab }) => {
  const hasHistory = Array.isArray(t.trade_history) && t.trade_history.length > 0;
  const ltp = t.posted_time_ltp;

  // previous_data rows don't carry ltp/pl, and exited trades don't track live.
  // Futures tick on the contract symbol (fut_symbol), not the plain underlying symbol_name.
  const wsSymbol = t.fut_symbol || t.symbol_name;
  const { ltp: liveLtp, isConnected: wsIsConnected } = usePartnersWS({
    symbol: wsSymbol,
    enabled: showLiveColumns && !t.exit,
  });

  useEffect(() => {
    if (showLiveColumns && !t.exit) {
      console.log(`[WS] subscribed=${wsSymbol} connected=${wsIsConnected} liveLtp=${liveLtp}`);
    }
  }, [wsSymbol, wsIsConnected, liveLtp, showLiveColumns, t.exit]);

  const entryPrice = safeNum(t.entry_price);
  const lotSize = safeNum(t.lot_size);
  const livePnl =
    liveLtp != null && entryPrice != null && lotSize != null
      ? (t.signal === "short" ? entryPrice - liveLtp : liveLtp - entryPrice) * lotSize
      : null;
  const displayLtp = showLiveColumns && !t.exit ? liveLtp ?? safeNum(t.ltp) : null;
  const displayPnl = showLiveColumns && !t.exit ? livePnl ?? safeNum(t.pl) : null;

  const exitPrice = safeNum(t.exit_price);
  const finalPnl =
    t.exit && entryPrice != null && exitPrice != null && lotSize != null
      ? (t.signal === "short" ? entryPrice - exitPrice : exitPrice - entryPrice) * lotSize
      : null;

  // Profit as per the high (long) / low (short) touched since entry, vs the LTP at entry time.
  const entryTimeLtp = safeNum(t.posted_time_ltp);
  const isShort = t.signal === "short";
  const highLowRef = isShort ? safeNum(t.as_per_low) : safeNum(t.as_per_high);
  const profitAsPerHighLow =
    entryTimeLtp != null && highLowRef != null && lotSize != null
      ? (isShort ? entryTimeLtp - highLowRef : highLowRef - entryTimeLtp) * lotSize
      : null;

  return (
    <React.Fragment>
      <Box component="tr" sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
        <Box component="td" sx={{ p: "7px 5px", fontSize: 10, color: textMuted, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap", width: "24px" }}>{i + 1}</Box>
        <Box
          component="td"
          onClick={hasHistory ? () => toggleRow(t.id) : undefined}
          sx={{ p: "7px 5px", textAlign: "center", width: "14px", borderBottom: "1px solid #f1f5f9", cursor: hasHistory ? "pointer" : "default" }}
        >
          {hasHistory && (
            <Typography sx={{ fontSize: 10, color: brandGreen, fontWeight: 700 }}>{isExpanded ? "▲" : "▼"}</Typography>
          )}
        </Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{t.hold_by || "—"}</Box>
        {isExitedTab && (
          <Box component="td" sx={{ p: "7px 8px", fontSize: 10, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{exitReasonLabel(t.exit_reason)}</Box>
        )}
        <Box component="td" sx={{ p: "7px 8px", borderBottom: "1px solid #f1f5f9" }}>
          <Chip label={t.signal || "—"} color={SIGNAL_COLORS[t.signal] || textMuted} />
        </Box>
        <Box component="td" sx={{ p: "7px 8px", borderBottom: "1px solid #f1f5f9" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: textMain, whiteSpace: "nowrap" }}>{t.symbol_name}</Typography>
          <Typography sx={{ fontSize: 9.5, color: textMuted, whiteSpace: "nowrap" }}>{t.future_symbol || t.fut_symbol}</Typography>
        </Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{t.lot_size || "—"}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.entry_price)}</Box>
        {showLiveColumns && (
          <>
            <Box component="td" sx={{ p: "7px 8px", fontSize: 11, fontWeight: liveLtp != null ? 700 : 400, color: liveLtp != null ? brandGreen : textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(displayLtp)}</Box>
            <Box component="td" sx={{ p: "7px 8px", fontSize: 11, fontWeight: displayPnl != null ? 700 : 400, color: displayPnl == null ? textMain : displayPnl >= 0 ? brandGreen : negRed, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(displayPnl)}</Box>
          </>
        )}
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.target_price)}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.stop_loss)}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(ltp)}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 10, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtDate(t.created_at)}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, fontWeight: profitAsPerHighLow != null ? 700 : 400, color: profitAsPerHighLow == null ? textMain : profitAsPerHighLow >= 0 ? brandGreen : negRed, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(profitAsPerHighLow)}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: brandGreen, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.as_per_high)}</Box>
        <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: negRed, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.as_per_low)}</Box>
        {isExitedTab && (
          <>
            <Box component="td" sx={{ p: "7px 8px", fontSize: 10, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtDate(t.exit_time)}</Box>
            <Box component="td" sx={{ p: "7px 8px", fontSize: 11, fontWeight: finalPnl != null ? 700 : 400, color: finalPnl == null ? textMain : finalPnl >= 0 ? brandGreen : negRed, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(finalPnl)}</Box>
          </>
        )}
        {isExitedTab && (
          <Box component="td" sx={{ p: "7px 8px", fontSize: 11, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(t.exit_price)}</Box>
        )}
        {!isExitedTab && (
          <Box component="td" sx={{ p: "7px 8px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {t.hold_by ? (
                <Chip label="Taken" color={textMuted} />
              ) : (
                <Box
                  component="button"
                  onClick={() => handleTake(t.id)}
                  disabled={takingId === t.id || t.exit}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.5,
                    background: brandGreen, color: "#fff", border: "none", borderRadius: "6px",
                    padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer",
                    "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
                    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                  }}
                >
                  {takingId === t.id ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : "Take"}
                </Box>
              )}

              {t.posted ? (
                <Chip label="Posted" color={textMuted} />
              ) : (
                <Box
                  component="button"
                  title="Post to Future Dashboard"
                  onClick={() => onPost(t.id)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.5,
                    background: "#f0faf7", color: brandGreen, border: `1px solid ${brandGreen}`,
                    borderRadius: "6px",
                    padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer",
                    "&:hover": { background: brandGreen, color: "#fff" },
                  }}
                >
                  Post
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {hasHistory && isExpanded && (
        <Box component="tr">
          <Box component="td" colSpan={getVisibleColumns(showLiveColumns, isExitedTab).length} sx={{ p: 0, background: "#f0fdf4", borderBottom: "2px solid #bbf7d0" }}>
            <Box sx={{ p: "12px 20px", display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {t.trade_history.map((h, hi) => (
                <Box
                  key={hi}
                  sx={{
                    background: "#fff", border: "1.5px solid #bbf7d0", borderRadius: "8px",
                    padding: "8px 14px", display: "flex", gap: 2, alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 10, color: textMuted, fontWeight: 700, textTransform: "uppercase" }}>Rev {hi + 1}</Typography>
                  <Typography sx={{ fontSize: 10, color: textMain, fontFamily: "monospace" }}>Entry: {fmtRs(h.entry_price)}</Typography>
                  <Typography sx={{ fontSize: 10, color: negRed, fontFamily: "monospace" }}>SL: {fmtRs(h.stop_loss)}</Typography>
                  <Typography sx={{ fontSize: 10, color: brandGreen, fontFamily: "monospace" }}>Target: {fmtRs(h.target_price)}</Typography>
                  <Typography sx={{ fontSize: 9.5, color: textMuted }}>{fmtDate(h.created_at)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};

const FutureRecommendations = () => {
  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState([]);
  const [previousTrades, setPreviousTrades] = useState([]);
  const [dayFilter, setDayFilter] = useState("Today");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Open");
  const [takingId, setTakingId] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postForm, setPostForm] = useState(null);

  const openPostDialog = async (tradeId) => {
    setPostDialogOpen(true);
    setPostLoading(true);
    setPostForm(null);
    try {
      const res = await axiosConfig({
        method: "get",
        url: POST_DASHBOARD_URL,
        params: { trade_id: tradeId },
      });
      const detail = res?.data?.message?.[0];
      if (!detail) throw new Error("No data returned for this trade");
      setPostForm({
        trade_id: detail.id,
        client_signal_id: detail.client_signal_id,
        fut_symbol: detail.fut_symbol,
        future_symbol: detail.future_symbol,
        hold_by_id: detail.hold_by_id,
        hold_at: detail.hold_at,
        symbol_id: detail.symbol_id,
        segment: detail.segment,
        entry_price: detail.entry_price,
        posted_time_ltp: detail.posted_time_ltp,
        lot_size: detail.lot_size,
        lot_expiry: detail.lot_expiry,
        signal: detail.signal,
        created_at: detail.created_at,
        modified_at: detail.modified_at,
        target_price: detail.target_price ?? "",
        stop_loss: detail.stop_loss ?? "",
      });
    } catch (error) {
      console.log("posting-future-recommendation-to-future-dashboard GET error: ", error);
      toast.error(error?.response?.data?.response || error.message || "Failed to load trade details");
      setPostDialogOpen(false);
    } finally {
      setPostLoading(false);
    }
  };

  const closePostDialog = () => {
    if (!posting) {
      setPostDialogOpen(false);
      setPostForm(null);
    }
  };

  const updatePostField = (field, rawValue) => {
    const value = rawValue.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    setPostForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePostSubmit = async () => {
    setPosting(true);
    try {
      await axiosConfig({
        method: "post",
        url: POST_DASHBOARD_URL,
        data: {
          trade_id: postForm.trade_id,
          fut_symbol: postForm.fut_symbol,
          future_symbol: postForm.future_symbol,
          symbol_id: postForm.symbol_id,
          segment: postForm.segment,
          entry_price: String(postForm.entry_price),
          lot_size: Number(postForm.lot_size),
          lot_expiry: postForm.lot_expiry,
          signal: postForm.signal,
          target_price: postForm.target_price === "" ? null : Number(postForm.target_price),
          stop_loss: postForm.stop_loss === "" ? null : Number(postForm.stop_loss),
        },
      });
      toast.success("Posted to future dashboard");
      closePostDialog();
      loadTrades();
    } catch (error) {
      console.log("posting-future-recommendation-to-future-dashboard POST error: ", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.response ||
          "Failed to post to future dashboard",
      );
    } finally {
      setPosting(false);
    }
  };

  const loadTrades = async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/futures-recommendation-trades/",
      });
      setTrades(Array.isArray(res.data?.data) ? res.data.data : []);
      setPreviousTrades(Array.isArray(res.data?.previous_data) ? res.data.previous_data : []);
    } catch (error) {
      console.log("futures-recommendation-trades error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load future recommendations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrades();
  }, []);

  const isToday = dayFilter === "Today";
  const dayTrades = isToday ? trades : previousTrades;
  const isExitedTab = statusFilter === "Exited";
  // LTP/P&L only make sense for today's open trades — previous_data rows don't carry
  // those fields, and exited trades don't track live.
  const showLiveColumns = isToday && !isExitedTab;
  const visibleColumns = useMemo(() => getVisibleColumns(showLiveColumns, isExitedTab), [showLiveColumns, isExitedTab]);

  useEffect(() => {
    if (!showLiveColumns) return;
    const symbols = dayTrades.filter((t) => !t.exit).map((t) => t.fut_symbol || t.symbol_name).filter(Boolean);
    if (symbols.length > 0) partnersWSManager.bulkSubscribe(symbols);
  }, [dayTrades, showLiveColumns]);

  const handleTake = async (tradeId) => {
    setTakingId(tradeId);
    try {
      await axiosConfig({
        method: "post",
        url: "/futures-recommendation-trade/hold/",
        data: { trade_id: tradeId },
      });
      toast.success("Trade taken successfully!");
      loadTrades();
    } catch (error) {
      console.log("take future trade error: ", error);
      toast.error(error?.response?.data?.response || "Failed to take trade");
    } finally {
      setTakingId(null);
    }
  };

  const filteredTrades = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dayTrades
      .filter((t) => (statusFilter === "Exited" ? t.exit : !t.exit))
      .filter((t) => !q || Object.values(t || {}).some((v) => String(v ?? "").toLowerCase().includes(q)))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [dayTrades, search, statusFilter]);

  const handleDownload = () => {
    if (!filteredTrades.length) {
      toast.error("No recommendations to download");
      return;
    }
    const rows = filteredTrades.map((t) => ({
      Symbol: t.symbol_name,
      "Fut Symbol": t.fut_symbol,
      "Future Symbol": t.future_symbol,
      Segment: t.segment,
      Signal: t.signal,
      Status: t.exit ? "Exited" : "Open",
      "Entry Price": t.entry_price,
      LTP: t.posted_time_ltp,
      "Stop Loss": t.stop_loss,
      Target: t.target_price,
      "As Per High": t.as_per_high,
      "As Per Low": t.as_per_low,
      "Lot Size": t.lot_size,
      "Lot Expiry": fmtExpiry(t.lot_expiry),
      "Recommended On": fmtDate(t.created_at),
      "Exit Price": t.exit_price ?? "—",
      "Exit Reason": exitReasonLabel(t.exit_reason),
      "Exit Time": fmtDate(t.exit_time),
      "Hold By": t.hold_by || "—",
      "Hold At": fmtDate(t.hold_at),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Futures Recommendations");
    XLSX.writeFile(workbook, `futures-recommendations-${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Downloaded successfully!");
  };

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: navy,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <CandlestickChartIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Futures Recommendations
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              All futures trade recommendations
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Box
            component="button"
            onClick={loadTrades}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
              padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,0.18)" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
          </Box>
          <Box
            component="button"
            onClick={handleDownload}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
              padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 16 }} /> Download Excel
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: brandGreen }} />
        </Box>
      ) : (
        <>
          {/* Search */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search symbol"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ background: cardBg, borderRadius: "8px", minWidth: 260 }}
            />
          </Box>

          {/* Day tabs */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", mb: 2 }}>
            <Tabs
              value={dayFilter}
              onChange={(e, val) => setDayFilter(val)}
              sx={{
                minHeight: 44,
                px: 1,
                "& .MuiTabs-indicator": { backgroundColor: brandGreen, height: 3 },
              }}
            >
              {["Today", "Previous"].map((d) => (
                <Tab
                  key={d}
                  value={d}
                  label={d}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 44,
                    color: textMuted,
                    "&.Mui-selected": { color: navy },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Status tabs */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px 12px 0 0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", borderBottom: "none" }}>
            <Tabs
              value={statusFilter}
              onChange={(e, val) => setStatusFilter(val)}
              sx={{
                minHeight: 44,
                px: 1,
                "& .MuiTabs-indicator": { backgroundColor: brandGreen, height: 3 },
              }}
            >
              {["Open", "Exited"].map((s) => (
                <Tab
                  key={s}
                  value={s}
                  label={s}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 44,
                    color: textMuted,
                    "&.Mui-selected": { color: STATUS_COLORS[s] || navy },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Table */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "0 0 12px 12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh" }}>
              <Box component="table" sx={{ borderCollapse: "collapse", tableLayout: "fixed" }}>
                <Box component="thead">
                  <Box component="tr">
                    {visibleColumns.map((c, idx) => (
                      <Box
                        component="th"
                        key={c.label || `col-${idx}`}
                        sx={{
                          textAlign: "left", fontSize: 9.5, color: "#fff", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.2px",
                          background: navy, p: "8px 8px", whiteSpace: "normal", wordBreak: "normal", overflowWrap: "normal", lineHeight: 1.2,
                          position: "sticky", top: 0, zIndex: 2,
                          width: c.width || "auto",
                        }}
                      >
                        {c.label}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredTrades.length === 0 ? (
                    <Box component="tr">
                      <Box component="td" colSpan={visibleColumns.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                        No futures recommendations found
                      </Box>
                    </Box>
                  ) : (
                    filteredTrades.map((t, i) => (
                      <FutureRow
                        key={t.id}
                        t={t}
                        i={i}
                        isExpanded={!!expandedRows[t.id]}
                        toggleRow={toggleRow}
                        takingId={takingId}
                        handleTake={handleTake}
                        onPost={openPostDialog}
                        showLiveColumns={showLiveColumns}
                        isExitedTab={isExitedTab}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Dialog
        open={postDialogOpen}
        onClose={closePostDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "85vh",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: navy,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: "10px",
            px: 3,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Post to Future Dashboard
          </Typography>
          <IconButton onClick={closePostDialog} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            pt: "24px !important",
            pb: "24px !important",
            overflowY: "auto",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {postLoading || !postForm ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: brandGreen }} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <ReadOnlyField label="Client Signal ID" value={postForm.client_signal_id} />
              <ReadOnlyField label="Fut Symbol" value={postForm.fut_symbol} />
              <ReadOnlyField label="Future Symbol" value={postForm.future_symbol} />
              <ReadOnlyField label="Symbol ID" value={postForm.symbol_id} />
              <ReadOnlyField label="Segment" value={postForm.segment} />
              <ReadOnlyField label="Signal" value={postForm.signal} />
              <ReadOnlyField label="Entry Price" value={postForm.entry_price} />
              <ReadOnlyField label="Posted Time LTP" value={postForm.posted_time_ltp} />
              <ReadOnlyField label="Lot Size" value={postForm.lot_size} />
              <ReadOnlyField label="Lot Expiry" value={fmtExpiry(postForm.lot_expiry)} />
              <ReadOnlyField label="Created At" value={fmtDate(postForm.created_at)} />
              <ReadOnlyField label="Modified At" value={fmtDate(postForm.modified_at)} />

              <TextField
                label="Target Price"
                value={postForm.target_price}
                onChange={(e) => updatePostField("target_price", e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                label="Stop Loss"
                value={postForm.stop_loss}
                onChange={(e) => updatePostField("stop_loss", e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexShrink: 0, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={closePostDialog} sx={{ color: textMuted }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePostSubmit}
            disabled={posting || postLoading || !postForm}
            sx={{
              background: brandGreen,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {posting ? "Posting..." : "Post"}
          </Button>
        </DialogActions>
      </Dialog>

      <ReactHotToaster />
    </Box>
  );
};

export default FutureRecommendations;
